/**
 * The workspace (chat mode) a turn ran in, as persisted in
 * `chat_messages.message_metadata.mode` (Story #27707).
 *
 * The admin report card breaks a user's activity down by workspace, and the
 * mode existed only in the chat-v3 request body until this was recorded. Both
 * writers stamp it: use-chat.ts on the user message, and chat-v3 (plus
 * use-chat's own assistant save, which can win the dual-write race) on the
 * assistant reply. Read by backend/app/services/admin_people.py, which keys on
 * the same `mode` name.
 *
 * Isomorphic: imported by the chat-v3 route handler and by the browser hook.
 */

import type { ChatMode } from '@/lib/ai/prompts/routing'

export const MESSAGE_MODE_KEY = 'mode'

// Record<ChatMode, true> makes the compiler reject a missing or extra mode, so
// this set cannot drift from the ChatMode union.
const CHAT_MODES: Record<ChatMode, true> = {
  concierge: true,
  'study-design': true,
  norma: true,
  val: true,
  testcases: true,
  marq: true,
  compli: true,
}

export function isChatMode(value: unknown): value is ChatMode {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(CHAT_MODES, value)
}

/**
 * `metadata` with the turn's mode added. The chat-v3 body is unvalidated
 * JSON, so anything that is not a known chat mode is left out rather than
 * written: an unknown value would show up as a workspace on the report card.
 */
export function withTurnMode<T extends Record<string, unknown>>(
  metadata: T,
  mode: unknown,
): T & { mode?: ChatMode } {
  if (!isChatMode(mode)) return metadata
  return { ...metadata, [MESSAGE_MODE_KEY]: mode }
}
